import { API_CONFIG } from '../config/constants';
import { eventCentral } from './eventCentral';
import socketService from './socketService';

export interface InvoiceDocument {
	url: string;
	fileName?: string;
	invoiceNumber: string;
	expiresInSeconds?: number;
}

/**
 * Request a generated tax invoice from the authenticated C1 backend.
 *
 * AL-326: keyed on the PAYMENT, not on an invoice number. A payment that has never been invoiced has
 * no number to name, so asking by number could never obtain a first invoice — the backend mints one
 * when the payment has none. Asking by payment also works for an invoice whose number was assigned
 * manually. The payment is resolved only within the organisation on the authenticated connection.
 *
 * No format check here. It previously required /^\d{9,10}$/ and refused anything else before a request
 * was even sent, which made a legitimately issued invoice carrying a manual reference undownloadable.
 */
export async function fetchInvoiceDocument(
	paymentId: string,
): Promise<InvoiceDocument> {
	if (!paymentId || !paymentId.trim()) {
		throw new Error('A payment is required to download a tax invoice.');
	}

	const response = await eventCentral.sendRequest(
		socketService,
		'DocumentRequest',
		{ action: 'GET_INVOICE', paymentId },
		API_CONFIG.REQUEST_TIMEOUT,
	);

	const document = response.DocumentResponse ?? response.data ?? response;
	const url = document?.url ?? document?.downloadUrl ?? '';
	if (!url) {
		throw new Error(
			document?.statusText ??
			response.Status?.description ??
			'The tax invoice could not be prepared. Please try again.',
		);
	}

	return {
		url,
		fileName: document.fileName,
		// The number the backend issued or already held. On a first download this is the value it has
		// just minted, which the caller has no way of knowing in advance.
		invoiceNumber: document.invoiceNumber ?? '',
		expiresInSeconds: document.expiresInSeconds,
	};
}
